import { useState } from "react";
import { toast } from "sonner";
import { KeyRound, Loader2, ShieldCheck, CheckCircle2, Circle, Eye, EyeOff } from "lucide-react";

// Non-dismissable: shown on first login with a temporary password. No close button, no escape.
export const ForcePasswordModal = ({ api, email, onDone }) => {
  const [form, setForm] = useState({ next: "", confirm: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [show, setShow] = useState(false);

  const pw = form.next;
  const checks = {
    length: pw.length >= 5,
    capital: /^[A-Z]/.test(pw),
    special: /[^A-Za-z0-9]/.test(pw),
  };
  const passed = Object.values(checks).filter(Boolean).length;
  const hasLen8 = pw.length >= 8;
  const score = pw ? Math.min(4, passed + (hasLen8 ? 1 : 0)) : 0;
  const strength = ["", "Weak", "Fair", "Good", "Strong"][score];
  const barColor = ["bg-white/10", "bg-red-500", "bg-amber-500", "bg-lime-500", "bg-emerald-500"][score];
  const strengthText = ["", "text-red-400", "text-amber-400", "text-lime-400", "text-emerald-400"][score];

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    const pw = form.next;
    if (pw.length < 5) {
      setErr("Password must be at least 5 characters");
      return;
    }
    if (!/^[A-Z]/.test(pw)) {
      setErr("The first letter must be a capital letter");
      return;
    }
    if (!/[^A-Za-z0-9]/.test(pw)) {
      setErr("Include at least one special character such as @ # $ %");
      return;
    }
    if (form.next !== form.confirm) {
      setErr("Passwords do not match");
      return;
    }
    setBusy(true);
    try {
      await api.post("/mail/password/change", { new_password: form.next });
      toast.success("Password set — welcome to NexSecure Mail");
      onDone();
    } catch (error) {
      const d = error?.response?.data?.detail;
      let msg = "Could not set the password — try again";
      if (typeof d === "string") msg = d;
      else if (Array.isArray(d) && d[0]?.msg) msg = d[0].msg;
      setErr(msg);
    } finally {
      setBusy(false);
    }
  };

  const input =
    "w-full bg-white/[0.04] border border-white/15 text-slate-100 placeholder:text-slate-500 px-4 py-3.5 text-sm focus:outline-none focus:border-nx-goldlight/70 transition-colors";

  return (
    <div data-lenis-prevent className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-nx-navy/95" data-testid="force-password-modal">
      <div className="bg-nx-navy2 border border-white/10 w-full max-w-sm p-7">
        <div className="flex items-center gap-3 mb-2">
          <span className="w-10 h-10 border border-nx-gold/50 bg-nx-gold/10 flex items-center justify-center text-nx-goldlight"><KeyRound size={18} /></span>
          <h2 className="font-marcellus text-lg text-white">Set your new password</h2>
        </div>
        <p className="text-xs text-slate-400 leading-relaxed mb-6">
          You signed in with a temporary password for <span className="text-slate-200">{email}</span>. Choose your own password to continue — this step cannot be skipped.
        </p>
        <form onSubmit={submit} className="flex flex-col gap-4">
          <div className="border border-nx-gold/25 bg-nx-gold/[0.06] px-4 py-3" data-testid="force-password-rules">
            <p className="text-[10px] tracking-[0.2em] uppercase text-nx-goldlight mb-2">Password rules</p>
            <ul className="text-xs space-y-1.5">
              {[
                ["length", "Minimum 5 characters"],
                ["capital", "First letter must be a capital"],
                ["special", "At least one special character such as @ # $ %"],
              ].map(([k, txt]) => (
                <li key={k} className={`flex items-start gap-2 transition-colors ${checks[k] ? "text-emerald-400" : "text-slate-400"}`} data-testid={`force-password-rule-${k}`}>
                  {checks[k] ? <CheckCircle2 size={14} className="mt-px shrink-0" /> : <Circle size={14} className="mt-px shrink-0 text-slate-600" />}
                  {txt}
                </li>
              ))}
            </ul>
          </div>
          <div>
            <label className="block text-[10px] tracking-[0.2em] uppercase text-slate-500 mb-2">New password</label>
            <div className="relative">
              <input required type={show ? "text" : "password"} minLength={5} autoComplete="new-password" className={`${input} pr-11`} value={form.next} onChange={(e) => setForm({ ...form, next: e.target.value })} data-testid="force-password-new" />
              <button type="button" onClick={() => setShow((s) => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-nx-goldlight transition-colors" data-testid="force-password-toggle" aria-label={show ? "Hide password" : "Show password"}>
                {show ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
            {pw && (
              <div className="mt-2" data-testid="force-password-strength">
                <div className="h-1.5 w-full rounded-full bg-white/10 overflow-hidden flex gap-1">
                  {[1, 2, 3, 4].map((i) => (
                    <div key={i} className={`h-full flex-1 rounded-full transition-colors ${i <= score ? barColor : "bg-white/10"}`} />
                  ))}
                </div>
                <p className={`text-[11px] mt-1 ${strengthText}`}>Strength: {strength}</p>
              </div>
            )}
          </div>
          <div>
            <label className="block text-[10px] tracking-[0.2em] uppercase text-slate-500 mb-2">Confirm new password</label>
            <div className="relative">
              <input required type={show ? "text" : "password"} minLength={5} autoComplete="new-password" className={`${input} pr-11`} value={form.confirm} onChange={(e) => setForm({ ...form, confirm: e.target.value })} data-testid="force-password-confirm" />
              <button type="button" onClick={() => setShow((s) => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-nx-goldlight transition-colors" aria-label={show ? "Hide password" : "Show password"}>
                {show ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>
          {err && <p className="text-xs text-red-400" data-testid="force-password-error">{err}</p>}
          <button type="submit" disabled={busy} className="flex items-center justify-center gap-2 bg-nx-gold hover:bg-nx-goldlight disabled:opacity-60 text-nx-navy text-xs font-semibold tracking-[0.14em] uppercase px-5 py-3.5 transition-colors" data-testid="force-password-submit">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <ShieldCheck size={14} />} Set password & continue
          </button>
          <p className="text-[10px] text-slate-600 text-center">The temporary password stops working the moment your new password is set.</p>
        </form>
      </div>
    </div>
  );
};
